import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { DoNotDisturbOnOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useActingStoreAdmin } from '../hooks/useActingStoreAdmin';
import { useToast } from '../contexts/ToastContext';
import { useUnsavedChangesSource } from './appUpdate/unsavedChanges';
import { BrandMultiSelect, InlineMessage, LoadingState, SaveBar, SectionCard } from './ui';

export interface BrandPricingExclusionsSetting {
  rebateBrands: string[];
  saleBrands: string[];
  updatedAt: string | null;
}

export const BRAND_PRICING_EXCLUSIONS_QUERY_KEY = ['store-brand-pricing-exclusions'] as const;
const ENDPOINT = '/store/settings/brand-pricing-exclusions';

/** Same brands in any order and case. */
export const sameBrands = (a: string[], b: string[]): boolean => {
  const left = new Set(a.map((x) => x.trim().toLowerCase()));
  const right = new Set(b.map((x) => x.trim().toLowerCase()));
  return left.size === right.size && Array.from(left).every((x) => right.has(x));
};

/**
 * Jordan names the brands the store does not run rebates on, and the brands it
 * does not run sale prices on (flag `brand_pricing_exclusions`, MOT-734). Every
 * connected store then lists those brands at the regular price. Owner/admin edit;
 * everyone else reads.
 */
export const BrandPricingExclusionsCard: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { enabled } = useOrgFeatureFlag('brand_pricing_exclusions');
  const { canManage } = useActingStoreAdmin();
  const [rebateBrands, setRebateBrands] = useState<string[]>([]);
  const [saleBrands, setSaleBrands] = useState<string[]>([]);

  const query = useQuery({
    queryKey: BRAND_PRICING_EXCLUSIONS_QUERY_KEY,
    queryFn: async (): Promise<BrandPricingExclusionsSetting> => {
      const response = await makeApiCall(ENDPOINT, { headers: await getAuthHeaders() });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) throw new Error(body?.error?.message || body?.message || t('store.brandExclusions.loadError'));
      return body.data as BrandPricingExclusionsSetting;
    },
    enabled,
  });
  const data = query.data;

  const discard = React.useCallback(() => {
    setRebateBrands(data?.rebateBrands ?? []);
    setSaleBrands(data?.saleBrands ?? []);
  }, [data]);
  useEffect(() => {
    discard();
  }, [discard]);

  const dirty = useMemo(
    () => canManage && !!data && (!sameBrands(data.rebateBrands, rebateBrands) || !sameBrands(data.saleBrands, saleBrands)),
    [canManage, data, rebateBrands, saleBrands],
  );

  const save = useMutation({
    mutationFn: async () => {
      const response = await makeApiCall(ENDPOINT, {
        method: 'PUT',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ rebateBrands, saleBrands }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) throw new Error(body?.error?.message || body?.message || t('store.brandExclusions.saveError'));
      return body.data as BrandPricingExclusionsSetting;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(BRAND_PRICING_EXCLUSIONS_QUERY_KEY, saved);
      queryClient.invalidateQueries({ queryKey: ['store-pricing-breakdown'] });
      showToast(t('store.brandExclusions.saved'), 'success');
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });
  useUnsavedChangesSource({ dirty, save: () => save.mutateAsync(), discard });

  if (!enabled) return null;

  const notListed = t('store.brandExclusions.notListed');
  return (
    <SectionCard title={t('store.brandExclusions.title')} leading={<DoNotDisturbOnOutlined color="primary" />}>
      {query.isLoading ? (
        <LoadingState />
      ) : query.isError || !data ? (
        <InlineMessage severity="error">{t('store.brandExclusions.loadError')}</InlineMessage>
      ) : (
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            {t('store.brandExclusions.intro')}
          </Typography>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t('store.brandExclusions.rebateLabel')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('store.brandExclusions.rebateHelp')}
            </Typography>
            <BrandMultiSelect
              value={rebateBrands}
              onChange={setRebateBrands}
              disabled={!canManage}
              placeholder={t('store.brandExclusions.placeholder')}
              notListedMessage={notListed}
              aria-label={t('store.brandExclusions.rebateLabel')}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t('store.brandExclusions.saleLabel')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('store.brandExclusions.saleHelp')}
            </Typography>
            <BrandMultiSelect
              value={saleBrands}
              onChange={setSaleBrands}
              disabled={!canManage}
              placeholder={t('store.brandExclusions.placeholder')}
              notListedMessage={notListed}
              aria-label={t('store.brandExclusions.saleLabel')}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t(canManage ? 'store.brandExclusions.overrideNote' : 'store.brandExclusions.readOnly')}
          </Typography>
          {canManage && (
            <SaveBar
              dirty={dirty}
              message={t('store.brandExclusions.saveMessage')}
              saving={save.isPending}
              onSave={() => save.mutate()}
              onDiscard={discard}
            />
          )}
        </Stack>
      )}
    </SectionCard>
  );
};

export default BrandPricingExclusionsCard;
